<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhPhone :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Phone Number</h2>
                    <p>{{ isEditing ? 'Update' : 'Add' }} a phone number for this employee</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Phone Number</label>
                    <PhoneFields v-model="phoneValue" @update:valid="isPhoneValid = $event" />
                </div>
            </div>

            <div class="modal-footer modal-footer--split">
                <PrimeButton
                    v-if="isEditing"
                    label="Delete"
                    severity="danger"
                    text
                    @click="handleDelete"
                    :disabled="isLoading"
                />
                <div class="flex-1" />
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton
                    label="Save"
                    type="submit"
                    :loading="isLoading"
                    :disabled="!canSubmit"
                />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhPhone } from '@phosphor-icons/vue';
import PhoneFields, { type PhoneValue } from '~/components/shared/forms/PhoneFields.vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.EDIT_EMPLOYEE_PHONE);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const companyId = computed(() => dialogRef.value?.data?.companyId);
const employeeId = computed(() => dialogRef.value?.data?.employeeId);
const phone = computed(() => dialogRef.value?.data?.phone);
const editingId = ref<number | undefined>(undefined);
const isEditing = computed(() => editingId.value !== undefined);

// The shared phone control owns the country list, the digit-only input, the
// formatting and the validity rule; this modal only owns what it sends.
const phoneValue = ref<PhoneValue>({ country_code: '1', phone_number: '' });
const isPhoneValid = ref(false);

const canSubmit = computed(() => isPhoneValid.value && !isLoading.value);

onMounted(() => {
    if (phone.value) {
        editingId.value = phone.value.id;
        phoneValue.value = {
            country_code: String(phone.value.country_code ?? '1').replace(/\D/g, '') || '1',
            phone_number: String(phone.value.phone_number ?? ''),
        };
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!canSubmit.value) {
        showError('Error', 'Please enter a valid phone number');
        return;
    }
    isLoading.value = true;
    try {
        await businessService.saveEmployeePhoneNumber(companyId.value, employeeId.value, {
            id: editingId.value,
            country_code: phoneValue.value.country_code,
            phone_number: phoneValue.value.phone_number.replace(/\D/g, ''),
        });
        showSuccess('Success', `Phone number ${isEditing.value ? 'updated' : 'added'} successfully`);
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to save phone number'));
    } finally {
        isLoading.value = false;
    }
};

const handleDelete = async () => {
    if (!editingId.value) return;
    isLoading.value = true;
    try {
        await businessService.removeEmployeePhoneNumber(companyId.value, employeeId.value, editingId.value);
        showSuccess('Success', 'Phone number removed successfully');
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to remove phone number'));
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';
</style>
